// CardDetail.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'; // Import useRef
import { useParams, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Heart, Plus, Check, Info, SlidersHorizontal } from 'lucide-react'; // Add icons
import { useAuth } from '../contexts/AuthContext'; // Add auth context
import { useCurrency } from '../contexts/CurrencyContext';
import { collectionAPI, wishlistAPI } from '../lib/api'; // Our API (user data in our Postgres)
import { roundPriceAmount } from '../lib/pricing';
import CollectionSelectorModal from '../components/CollectionSelectorModal';
import PriceInputModal from '../components/PriceInputModal';
import PriceChart from '../components/PriceChart';
import PriceHistoryModal from '../components/PriceHistoryModal';
import PriceDisplay from '../components/PriceDisplay';
import analyticsService from '../services/analyticsService';
import PageMeta from '../components/PageMeta';
import StorePhoto from '../components/StorePhoto';
import NearMeFilter, { formatDistanceKm, NearMeChip } from '../components/NearMeFilter';
import { loadPrefs, savePrefs, useNearMePref, useSortPref } from '../lib/listingPrefs';
import { imageFallback, sourceImageUrls } from '../lib/cardImages';
import { isProductCardId } from '../lib/games';
import GameCardPage from './GameCardPage';
import { checkedAgo, checkedTitle } from '../lib/listingFreshness';
import SeoExtras from '../components/SeoExtras';

// WO-35: the shared preference stores the product pages' sort names; this page keeps its own labels.
const POKEMON_SORT_LABELS = {
  'Most Recent': 'Most Recent', 'Lowest Price': 'Price: Low to High', 'Highest Price': 'Price: High to Low', Nearest: 'Nearest',
};
const POKEMON_SORT_PREFS = Object.fromEntries(Object.entries(POKEMON_SORT_LABELS).map(([k, v]) => [v, k]));

function CardDetail() {
  const { cardId: rawCardId } = useParams();
  const cardId = rawCardId;
  // WO-35: ids such as ex10-? (Unown ?) must be encoded in URLs; "?" started a query string and broke the page.
  const cardIdUrl = encodeURIComponent(cardId || '');
  const { user, profile } = useAuth(); // Add auth hook
  const { currency, convertCadAmount, convertToCadAmount, formatCurrencyAmount } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const [cardData, setCardData] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showPriceHistoryModal, setShowPriceHistoryModal] = useState(false);
  const [enlargedChartData, setEnlargedChartData] = useState(null);
  
  // Cache pricing data to persist across quick navigations
  const pricingCacheRef = useRef(new Map());
  const navigationContextRef = useRef(null);
  if (navigationContextRef.current === null) {
    navigationContextRef.current = (location.state && typeof location.state === 'object')
      ? location.state
      : analyticsService.getNavigationContext();
  }
  
  // New pricing state using consolidated API
  const [pricingData, setPricingData] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(false);
  const [viewMode, setViewMode] = useState('raw'); // raw | graded
  const [selectedFinish, setSelectedFinish] = useState('general');
  const [selectedGradeBucket, setSelectedGradeBucket] = useState(null);
  const [selectedSources, setSelectedSources] = useState(['market']);
  const [selectedGradedSources, setSelectedGradedSources] = useState(['market']);
  const [gradedAvailability, setGradedAvailability] = useState(null);
  const [gradedLoading, setGradedLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sourcePanelOpen, setSourcePanelOpen] = useState(false);
  const sourcePanelRef = useRef(null);
  
  // Collection/wishlist state
  const [inCollection, setInCollection] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modal state for collection picker + price input flow
  const [showCollectionSelector, setShowCollectionSelector] = useState(false);
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);
  const [showWishlistPricePrompt, setShowWishlistPricePrompt] = useState(false);

  // Filter states
  const [selectedListingTypes, setSelectedListingTypes] = useState(['ungraded']);
  const [selectedFinishVariants, setSelectedFinishVariants] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  // WO-35: conditions, seller types, sort and near me are remembered across cards and visits (lib/listingPrefs.js);
  // the rest of the filters belong to this card.
  const [selectedConditions, setSelectedConditions] = useState(() => loadPrefs().conditions || []);
  const [selectedSourceTypes, setSelectedSourceTypes] = useState(() => loadPrefs().sellers || []);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortPref, setSortPref] = useSortPref('Most Recent');
  const sortOrder = POKEMON_SORT_LABELS[sortPref] || 'Most Recent';
  const setSortOrder = useCallback((label, opts) => setSortPref(POKEMON_SORT_PREFS[label] || 'Most Recent', opts), [setSortPref]);
  const [listingSearch, setListingSearch] = useState('');
  // WO-13 near-me filter (store-level distance; off by default so existing behaviour is unchanged)
  const [nearMe, setNearMe, nearMeWanted] = useNearMePref();
  const [listingsLoaded, setListingsLoaded] = useState(false);
  const [nearMeStatus, setNearMeStatus] = useState('idle');
  const [nearbyById, setNearbyById] = useState(null);

  const [filterOpen, setFilterOpen] = useState({
    listingType: true,
    variant: true,
    grade: true,
    condition: true,
    location: true,
    source: true,
    price: true,
    nearMe: true,
  });
  const [locationOpen, setLocationOpen] = useState({});
  const [gradeOpen, setGradeOpen] = useState({});

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const cardName = cardData?.name;
  const cardNumber = cardData?.number;
  const printedTotal = cardData?.printedTotal || cardData?.set?.printedTotal;
  const numberLabel = cardNumber && printedTotal ? `${cardNumber}/${printedTotal}` : cardNumber || null;
  // WO-39: same title and canonical as the server-rendered page (lilycove-api services/seo.py build_pokemon_card).
  const title = cardName
    ? `${cardName}${numberLabel ? ` (${numberLabel})` : ''} Price in Canada | Lilycove`
    : cardId
      ? `Card ${cardId} | Lilycove`
      : 'Card | Lilycove';
  const description = cardName
    ? `View ${cardName}${numberLabel ? ` (${numberLabel})` : ''} on Lilycove.`
    : 'View card details on Lilycove.';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const ogImage = cardId && baseUrl ? `${baseUrl}/og/card/${cardIdUrl}.png` : undefined;
  const canonicalUrl = cardId ? `https://lilycove.io/card/${cardIdUrl}` : undefined;

  const gradeBucketLabels = {
    grade_7: 'Grade 7',
    grade_8: 'Grade 8',
    grade_9: 'Grade 9',
    grade_10: 'Grade 10'
  };

  const sourceLabels = {
    market: 'Market Price',
    shopify_active: 'Retail listing average',
    shopify_sold: 'Retail sold average',
    ebay_active: 'eBay listing average',
    ebay_sold: 'eBay ended listings average',
    tcgplayer: 'TCG Player'
  };

  const sourceColors = {
    market: '#00E5A0',
    shopify_active: '#4F46E5',
    shopify_sold: '#7C3AED',
    ebay_active: '#F59E0B',
    ebay_sold: '#F97316',
    tcgplayer: '#10B981'
  };

  const sourceOrder = [
    'market',
    'shopify_active',
    'shopify_sold',
    'ebay_active',
    'ebay_sold',
    'tcgplayer'
  ];

  const getPreferredGradeBucket = (availability) => {
    if (!availability || !availability.grade_buckets) return 'grade_9';
    const buckets = availability.grade_buckets;
    if (buckets.grade_9 && (buckets.grade_9.points > 0 || buckets.grade_9.listing_count > 0)) {
      return 'grade_9';
    }
    const entries = Object.entries(buckets)
      .filter(([, data]) => (data.points || 0) > 0 || (data.listing_count || 0) > 0)
      .sort((a, b) => (b[1].listing_count || 0) - (a[1].listing_count || 0));
    return entries.length > 0 ? entries[0][0] : 'grade_9';
  };

  const cardEntrySource = navigationContextRef.current?.came_from || 'direct';

  const getAvailableGradeBuckets = (availability) => {
    if (!availability || !availability.grade_buckets) return [];
    return Object.entries(availability.grade_buckets)
      .filter(([, data]) => (data.points || 0) > 0 || (data.listing_count || 0) > 0)
      .map(([bucket]) => bucket);
  };

  // Helper function to determine the correct image URL, mirroring PokemonCard.js logic
  const getDisplayImageUrl = (currentCard) => {
    const ghBase = 'https://raw.githubusercontent.com/Zahin-Mohammad-plug/lilycove-images/main';
    const localPlaceholder = '/placeholder-card.svg'; // Use local SVG placeholder

    // Function to attempt to correct casing for image paths
    const correctImagePathCasing = (path) => {
      if (typeof path === 'string' && path.startsWith('/images/cards/')) {
        const parts = path.split('/');
        const filenameWithExt = parts[parts.length - 1];
        const dotIndex = filenameWithExt.lastIndexOf('.');
        if (dotIndex === -1) return path;
        const filename = filenameWithExt.substring(0, dotIndex);
        const extension = filenameWithExt.substring(dotIndex);
        const hyphenIndex = filename.indexOf('-');
        if (hyphenIndex > -1 && hyphenIndex < filename.length - 1) {
          const prefix = filename.substring(0, hyphenIndex + 1);
          const suffix = filename.substring(hyphenIndex + 1);
          const correctedFilename = prefix + suffix.toUpperCase();
          return `/images/cards/${correctedFilename}${extension}`;
        }
      }
      return path;
    };
    
    // 1. Prioritize API images.large if available
    if (currentCard.images?.large) {
      return currentCard.images.large;
    }
    
    // 2. Prioritize API images.small if available
    if (currentCard.images?.small) {
      return currentCard.images.small;
    }
    
    // 3. Prioritize cardImage if available
    if (currentCard.cardImage) {
      if (currentCard.cardImage.startsWith('http://') || currentCard.cardImage.startsWith('https://')) {
        return currentCard.cardImage;
      } else {
        const correctedPath = correctImagePathCasing(currentCard.cardImage);
        const pathSegment = correctedPath.startsWith('/') ? correctedPath : `/${correctedPath}`;
        return ghBase + pathSegment;
      }
    }

    // 4. Fallback to LogoImg
    if (currentCard.LogoImg) {
      if (currentCard.LogoImg.startsWith('http://') || currentCard.LogoImg.startsWith('https://')) {
        return currentCard.LogoImg;
      } else {
        const correctedPath = correctImagePathCasing(currentCard.LogoImg);
        const pathSegment = correctedPath.startsWith('/') ? correctedPath : `/${correctedPath}`;
        return ghBase + pathSegment;
      }
    }
    
    // 5. Fallback to backupLogoUrl
    if (currentCard.backupLogoUrl) return currentCard.backupLogoUrl;
    
    // 6. Fallback to extLogoImg
    if (currentCard.extLogoImg) return currentCard.extLogoImg;
    
    return localPlaceholder;
  };

  useEffect(() => {
    // Create AbortController for cleanup
    const abortController = new AbortController();
    const signal = abortController.signal;
    
    // Fetch card details, listings, and pricing independently
    async function fetchCardDetails() {
      try {
        const response = await fetch(`${apiUrl}/api/card/${cardIdUrl}`, { signal });
        if (signal.aborted) return;
        
        if (!response.ok) {
          if (response.status === 404) setNotFound(true);   // WO-35: "Card not found", not "Unable to load"
          throw new Error('Failed to fetch card details');
        }
        const cardData = await response.json();
        
        if (signal.aborted) return;
        setCardData(cardData);
        
        // Track card view analytics
        analyticsService.trackCardView(
          cardData.id || cardData.cardId || cardId,
          cardEntrySource,
          cardData.setId || cardData.set_id || null
        )
          .catch(err => console.warn('Analytics tracking failed:', err));
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching card details:', error);
        if (!signal.aborted) {
          setError(true);
        }
      } finally {
        if (!signal.aborted) {
          setLoading(false); // Stop loading card details
        }
      }
    }

    async function fetchPricing() {
      try {
        // Check cache first (valid for 30 seconds)
        const cacheKey = cardId;
        const cached = pricingCacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < 30000) {
          if (!signal.aborted) {
            setPricingData(cached.data.pricingData);
            setGradedAvailability(cached.data.gradedAvailability || null);
            setPricingLoading(false);
            setGradedLoading(false);
          }
          return;
        }
        
        setPricingLoading(true);
        setGradedLoading(true);
        
        // Fetch both pricing endpoints in parallel with timeout to prevent hanging
        const fetchWithTimeout = (url, timeout = 8000) => {
          return Promise.race([
            fetch(url, { signal }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Request timeout')), timeout)
            )
          ]);
        };
        
        const [generalResponse, historyResponse, gradedAvailabilityResponse] = await Promise.all([
          fetchWithTimeout(`${apiUrl}/api/pricing/${cardIdUrl}/general`),
          fetchWithTimeout(`${apiUrl}/api/pricing/${cardIdUrl}/history?days=30&bucket=raw&finish=general&source=market`),
          fetchWithTimeout(`${apiUrl}/api/pricing/${cardIdUrl}/graded-availability`)
        ]);
        
        if (signal.aborted) return;
        
        let generalData = null;
        let historyData = null;
        
        // Process general pricing response
        if (generalResponse.ok) {
          const generalResult = await generalResponse.json();
          if (generalResult.success) {
            generalData = generalResult.data;
          }
        }
        
        // Process history response - handle gracefully if it fails
        if (historyResponse.ok) {
          try {
            const historyResult = await historyResponse.json();
            if (historyResult.success && historyResult.data) {
              historyData = historyResult.data;
            }
          } catch (parseError) {
            console.warn('Failed to parse history response:', parseError);
            // Continue without history data - don't block UI
          }
        }

        // Process graded availability response
        let availabilityData = null;
        if (gradedAvailabilityResponse.ok) {
          try {
            const availabilityResult = await gradedAvailabilityResponse.json();
            if (availabilityResult.success && availabilityResult.data) {
              availabilityData = availabilityResult.data;
            }
          } catch (parseError) {
            console.warn('Failed to parse graded availability response:', parseError);
          }
        }
                
        if (signal.aborted) return;
        
        // Combine data in format expected by frontend
        setGradedAvailability(availabilityData);

        if (generalData || historyData) {
          const pricingDataObj = {
            general: generalData,
            history: historyData,
            // Map to expected format for backward compatibility
            price: generalData?.price || null,
            confidence: generalData?.confidence || null,
            priceHistory: historyData?.data_points || []
          };
          
          // Cache the data
          pricingCacheRef.current.set(cacheKey, {
            data: {
              pricingData: pricingDataObj,
              gradedAvailability: availabilityData
            },
            timestamp: Date.now()
          });
          
          setPricingData(pricingDataObj);
        } else {
          console.warn('No pricing data available for card');
          setPricingError(true);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Silently handle errors - don't block UI or navigation
        // Pricing data is optional, so we continue without it
        console.warn('Error fetching pricing (non-blocking):', error.message);
        if (!signal.aborted) {
          // Set empty pricing data instead of error to prevent UI blocking
          setPricingData({
            general: null,
            history: null,
            price: null
          });
          setPricingError(false); // Don't show error state
        }
      } finally {
        if (!signal.aborted) {
          setPricingLoading(false);
          setGradedLoading(false);
        }
      }
    }

    // Start fetching data
    fetchCardDetails();
    fetchPricing();
    
    // Cleanup function to abort all ongoing requests
    return () => {
      abortController.abort();
    };
  }, [apiUrl, cardEntrySource, cardId, cardIdUrl]);

  useEffect(() => {
    if (!cardId) return;
    const abortController = new AbortController();
    const signal = abortController.signal;

    async function fetchListings() {
      try {
        const listingsResponse = await fetch(`${apiUrl}/api/card/${cardIdUrl}/listings`, { signal });

        if (signal.aborted) return;

        if (listingsResponse.ok) {
          const listingsData = await listingsResponse.json();
          if (!signal.aborted) {
            setListings(listingsData);
            setListingsLoaded(true);
          }
        } else {
          console.warn('Failed to fetch listing data');
          setListingsLoaded(true);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching listings:', error);
        setListingsLoaded(true);
      }
    }

    fetchListings();

    return () => {
      abortController.abort();
    };
  }, [cardId, cardIdUrl, apiUrl]);

  useEffect(() => {
    if (!gradedAvailability || !gradedAvailability.has_graded_data) return;
    const preferredBucket = getPreferredGradeBucket(gradedAvailability);
    if (!selectedGradeBucket) {
      setSelectedGradeBucket(preferredBucket);
    }
  }, [gradedAvailability, selectedGradeBucket]);

  useEffect(() => {
    if (viewMode !== 'graded' || !gradedAvailability || !gradedAvailability.has_graded_data) return;
    const availableBuckets = getAvailableGradeBuckets(gradedAvailability);
    const preferredBucket = getPreferredGradeBucket(gradedAvailability);
    if (!selectedGradeBucket || (availableBuckets.length > 0 && !availableBuckets.includes(selectedGradeBucket))) {
      setSelectedGradeBucket(preferredBucket);
    }
  }, [viewMode, gradedAvailability, selectedGradeBucket]);

  useEffect(() => {
    if (!gradedAvailability?.has_graded_data && viewMode === 'graded') {
      setViewMode('raw');
    }
  }, [gradedAvailability, viewMode]);

  useEffect(() => {
    setHistoryData(null);
  }, [viewMode, selectedFinish, selectedGradeBucket]);

  useEffect(() => {
    if (!sourcePanelOpen) return;
    const handleClickOutside = (event) => {
      if (sourcePanelRef.current && !sourcePanelRef.current.contains(event.target)) {
        setSourcePanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sourcePanelOpen]);

  useEffect(() => {
    if (
      !historyData
      && pricingData?.history
      && viewMode === 'raw'
      && selectedFinish === 'general'
    ) {
      setHistoryData(pricingData.history);
    }
  }, [historyData, pricingData, viewMode, selectedFinish]);

  useEffect(() => {
    const sources = historyData?.available_sources || [];
    if (sources.length === 0) return;
    const preferredSource = sources.includes('market') ? 'market' : sources[0];
    const gradedView = viewMode === 'graded';
    const normalizeSelection = (prev) => {
      const filtered = prev.filter((src) => sources.includes(src));
      const next = filtered.length > 0 ? filtered : [preferredSource];
      if (next.length === prev.length && next.every((value, idx) => value === prev[idx])) {
        return prev;
      }
      return next;
    };
    if (gradedView) {
      setSelectedGradedSources(normalizeSelection);
    } else {
      setSelectedSources(normalizeSelection);
    }
  }, [historyData, viewMode]);

  useEffect(() => {
    if (viewMode === 'graded') {
      setSelectedListingTypes(['graded']);
    } else {
      setSelectedListingTypes(['ungraded']);
    }
  }, [viewMode]);

  const handleHistoryLoaded = useCallback((data) => {
    setHistoryData(data);
  }, []);

  const handleHistoryLoading = useCallback((loading) => {
    setHistoryLoading(loading);
  }, []);

  const updateActiveSources = (nextSources) => {
    if (isGradedView) {
      setSelectedGradedSources(nextSources);
    } else {
      setSelectedSources(nextSources);
    }
  };

  const toggleSourceSelection = (sourceKey) => {
    const current = isGradedView ? selectedGradedSources : selectedSources;
    const fallbackSource = availableSourceOptions.includes('market')
      ? 'market'
      : (availableSourceOptions[0] || 'market');

    if (sourceKey === 'all') {
      if (allSourcesSelected) {
        updateActiveSources([fallbackSource]);
      } else {
        updateActiveSources(availableSourceOptions);
      }
      return;
    }

    const exists = current.includes(sourceKey);
    const next = exists
      ? current.filter((src) => src !== sourceKey)
      : [...current, sourceKey];
    if (next.length === 0) {
      updateActiveSources([fallbackSource]);
      return;
    }
    updateActiveSources(next);
  };

  const normalizeGradeValue = useCallback((grade) => {
    if (grade === null || grade === undefined) return null;
    const numeric = Number(grade);
    if (Number.isNaN(numeric)) return null;
    return numeric >= 20 ? numeric / 10 : numeric;
  }, []);

  const formatGradeValue = useCallback((value) => {
    if (value === null || value === undefined) return null;
    return Number.isInteger(value) ? `${value}` : value.toFixed(1);
  }, []);

  const getListingType = (listing) => {
    return listing.market_bucket === 'graded' ? 'graded' : 'ungraded';
  };

  const getFinishVariant = (finish) => {
    if (!finish) return 'Unspecified';
    const normalized = String(finish).toLowerCase();
    if (normalized === 'holofoil') return 'Holofoil';
    if (normalized === 'reverse_holofoil' || normalized === 'reverse') return 'Reverse';
    if (normalized === 'nonfoil' || normalized === 'normal' || normalized === 'general') return 'General';
    return 'Unspecified';
  };

  const getListingGradeInfo = useCallback((listing) => {
    const gradeValue = normalizeGradeValue(listing.grade);
    const formattedGrade = formatGradeValue(gradeValue);
    const graderLabel = listing.grader ? String(listing.grader).trim() : 'Unspecified';
    const gradeValueLabel = formattedGrade ? formattedGrade : 'Unspecified';
    let gradeLabel = null;
    if (listing.grader && formattedGrade) {
      gradeLabel = `${listing.grader} ${formattedGrade}`;
    } else if (listing.grader) {
      gradeLabel = listing.grader;
    } else if (formattedGrade) {
      gradeLabel = `Grade ${formattedGrade}`;
    }
    if (!gradeLabel) {
      if (graderLabel === 'Unspecified' && gradeValueLabel === 'Unspecified') {
        gradeLabel = 'Unspecified';
      } else {
        gradeLabel = `${graderLabel} ${gradeValueLabel}`.trim();
      }
    }
    return { graderLabel, gradeValueLabel, gradeLabel };
  }, [normalizeGradeValue, formatGradeValue]);

  const getListingConditionLabel = (listing) => {
    if (!listing.condition) return 'Unspecified';
    return listing.condition;
  };

  const isUnknownValue = useCallback((value) => {
    if (value === null || value === undefined) return true;
    const normalized = String(value).trim();
    if (!normalized) return true;
    const lowered = normalized.toLowerCase();
    return lowered === 'unknown' || lowered === 'n/a';
  }, []);

  const countryNameFromISO2 = useCallback((code) => {
    if (isUnknownValue(code)) return 'Unknown';
    const normalized = String(code).trim();
    if (normalized.length === 2) {
      const upper = normalized.toUpperCase();
      try {
        if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
          const display = new Intl.DisplayNames(['en'], { type: 'region' });
          const name = display.of(upper);
          if (name) return name;
        }
      } catch (error) {
        // Fallback below
      }
      const fallbackMap = {
        US: 'United States',
        CA: 'Canada',
        GB: 'United Kingdom',
        AU: 'Australia',
        JP: 'Japan',
        DE: 'Germany',
        FR: 'France',
        IT: 'Italy',
        ES: 'Spain',
        NL: 'Netherlands',
        SE: 'Sweden',
        NO: 'Norway',
        DK: 'Denmark',
        FI: 'Finland',
        IE: 'Ireland',
        NZ: 'New Zealand',
        CH: 'Switzerland',
        AT: 'Austria',
        BE: 'Belgium',
        PT: 'Portugal',
        BR: 'Brazil',
        MX: 'Mexico',
        SG: 'Singapore',
        HK: 'Hong Kong',
        KR: 'South Korea',
        CN: 'China',
        TW: 'Taiwan',
        PL: 'Poland'
      };
      return fallbackMap[upper] || upper;
    }
    return normalized;
  }, [isUnknownValue]);

  const getLocationParts = useCallback((listing) => {
    const countryRaw = listing.country_code;
    const regionRaw = listing.province_state;
    const cityRaw = listing.city;
    const countryLabel = countryNameFromISO2(countryRaw);
    return {
      country: countryLabel,
      region: isUnknownValue(regionRaw) ? 'Unknown' : regionRaw,
      city: isUnknownValue(cityRaw) ? 'Unknown' : cityRaw
    };
  }, [countryNameFromISO2, isUnknownValue]);

  // Only check wishlist status (lightweight single API call that returns items).
  const checkWishlistStatus = useCallback(async () => {
    if (!user) return
    try {
      const wishlistResponse = await wishlistAPI.getUserWishlist(user.id)
      if (wishlistResponse.data) {
        setInWishlist(wishlistResponse.data.some(item => item.card_id === cardId))
      }
    } catch (error) {
      console.error('Error checking wishlist status:', error)
    }
  }, [user, cardId])

  // Check if card is in user's wishlist on mount (lightweight).
  // Collection membership check is skipped on mount because GET /collections
  // doesn't return nested items — we'd need N+1 calls per collection to check.
  useEffect(() => {
    if (user && cardId) {
      checkWishlistStatus()
    }
  }, [user, cardId, checkWishlistStatus])

  useEffect(() => {
    if (!cardId || !nearMe.active || nearMe.lat === null || nearMe.lng === null) {
      setNearbyById(null);
      setNearMeStatus('idle');
      return undefined;
    }
    const abortController = new AbortController();
    setNearMeStatus('loading');
    const params = new URLSearchParams({
      near_lat: String(nearMe.lat),
      near_lng: String(nearMe.lng),
      radius_km: String(nearMe.radiusKm),
    });
    fetch(`${apiUrl}/api/card/${cardIdUrl}/listings?${params}`, { signal: abortController.signal })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
      .then((data) => {
        const byId = {};
        (Array.isArray(data) ? data : []).forEach((item) => {
          byId[item.itemId] = { distance_km: item.distance_km, store_location: item.store_location };
        });
        setNearbyById(byId);
        setNearMeStatus('ready');
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching nearby listings:', error);
        setNearbyById(null);
        setNearMeStatus('error');
      });
    return () => abortController.abort();
  }, [apiUrl, cardId, cardIdUrl, nearMe.active, nearMe.lat, nearMe.lng, nearMe.radiusKm]);

  useEffect(() => {
    if (!nearMe.active && sortOrder === 'Nearest') setSortOrder('Most Recent', { remember: false });
  }, [nearMe.active, sortOrder, setSortOrder]);

  const normalizedListings = useMemo(() => {
    return listings.map((listing) => {
      const listingType = getListingType(listing);
      const finishVariant = getFinishVariant(listing.finish);
      const gradeInfo = listingType === 'graded' ? getListingGradeInfo(listing) : null;
      const gradeLabel = listingType === 'graded' ? (gradeInfo?.gradeLabel || 'Graded') : null;
      const conditionLabel = listingType === 'ungraded' ? getListingConditionLabel(listing) : null;
      const sourceType = listing.source === 'ebay'
        ? 'eBay'
        : (listing.source === 'shopify' || listing.shopify_store ? 'Local Game Store' : (listing.source || 'Other'));
      const locationParts = getLocationParts(listing);
      const countryKey = `country:${locationParts.country}`;
      const regionKey = locationParts.region !== 'Unknown'
        ? `region:${locationParts.country}|${locationParts.region}`
        : null;
      const cityKey = locationParts.region !== 'Unknown' && locationParts.city !== 'Unknown'
        ? `city:${locationParts.country}|${locationParts.region}|${locationParts.city}`
        : null;
      const graderLabel = gradeInfo?.graderLabel || 'Unspecified';
      const gradeValueLabel = gradeInfo?.gradeValueLabel || 'Unspecified';
      const gradeKeys = listingType === 'graded'
        ? {
            grader: `grader:${graderLabel}`,
            grade: `grade:${graderLabel}|${gradeValueLabel}`,
          }
        : null;

      const nearby = nearbyById ? nearbyById[listing.itemId] : null;

      return {
        ...listing,
        distance_km: nearby ? nearby.distance_km : listing.distance_km,
        store_location: nearby ? nearby.store_location : listing.store_location,
        listingType,
        finishVariant,
        gradeLabel,
        graderLabel,
        gradeValueLabel,
        gradeKeys,
        conditionLabel,
        sourceType,
        locationParts,
        locationKeys: {
          country: countryKey,
          region: regionKey,
          city: cityKey
        }
      };
    });
  }, [listings, getListingGradeInfo, getLocationParts, nearbyById]);

  const applyFilters = useCallback((data, ignore = {}) => {
    return data.filter((listing) => {
      if (!ignore.listingType && selectedListingTypes.length > 0) {
        if (!selectedListingTypes.includes(listing.listingType)) return false;
      }

      if (!ignore.finishVariant && selectedFinishVariants.length > 0 && listing.listingType === 'ungraded') {
        if (!selectedFinishVariants.includes(listing.finishVariant)) return false;
      }

      if (!ignore.grade && selectedGrades.length > 0 && listing.listingType === 'graded') {
        const matches = selectedGrades.some((key) => {
          if (key.startsWith('grader:')) return listing.gradeKeys?.grader === key;
          if (key.startsWith('grade:')) return listing.gradeKeys?.grade === key;
          return false;
        });
        if (!matches) return false;
      }

      if (!ignore.condition && selectedConditions.length > 0 && listing.listingType === 'ungraded') {
        if (!listing.conditionLabel || !selectedConditions.includes(listing.conditionLabel)) return false;
      }

      if (!ignore.source && selectedSourceTypes.length > 0) {
        if (!selectedSourceTypes.includes(listing.sourceType)) return false;
      }

      if (!ignore.location && selectedLocations.length > 0) {
        const matches = selectedLocations.some((key) => {
          if (key.startsWith('country:')) return listing.locationKeys.country === key;
          if (key.startsWith('region:')) return listing.locationKeys.region === key;
          if (key.startsWith('city:')) return listing.locationKeys.city === key;
          return false;
        });
        if (!matches) return false;
      }

      if (!ignore.nearMe && nearMe.active && nearbyById) {
        if (!nearbyById[listing.itemId]) return false;
      }

      if (!ignore.search && listingSearch) {
        const text = listing.title || '';
        if (!text.toLowerCase().includes(listingSearch.toLowerCase())) return false;
      }

      if (!ignore.price) {
        const priceValue = Number.isFinite(listing.price) ? listing.price : null;
        const minDisplay = priceRange.min ? Number.parseFloat(priceRange.min) : null;
        const maxDisplay = priceRange.max ? Number.parseFloat(priceRange.max) : null;
        const minCad = Number.isFinite(minDisplay)
          ? roundPriceAmount(convertToCadAmount(minDisplay, currency), 2)
          : null;
        const maxCad = Number.isFinite(maxDisplay)
          ? roundPriceAmount(convertToCadAmount(maxDisplay, currency), 2)
          : null;
        if ((priceRange.min || priceRange.max) && priceValue === null) return false;
        if (minCad !== null && priceValue !== null && priceValue < minCad) return false;
        if (maxCad !== null && priceValue !== null && priceValue > maxCad) return false;
      }

      return true;
    });
  }, [
    selectedListingTypes,
    selectedFinishVariants,
    selectedGrades,
    selectedConditions,
    selectedSourceTypes,
    selectedLocations,
    listingSearch,
    nearMe.active,
    nearbyById,
    priceRange.min,
    priceRange.max,
    convertToCadAmount,
    currency
  ]);

  const filteredListings = useMemo(() => applyFilters(normalizedListings), [normalizedListings, applyFilters]);

  const listingTypeFacetSource = useMemo(() => applyFilters(normalizedListings, { listingType: true }), [normalizedListings, applyFilters]);
  const finishFacetSource = useMemo(() => applyFilters(normalizedListings, { finishVariant: true }), [normalizedListings, applyFilters]);
  const gradeFacetSource = useMemo(() => applyFilters(normalizedListings, { grade: true }), [normalizedListings, applyFilters]);
  const conditionFacetSource = useMemo(() => applyFilters(normalizedListings, { condition: true }), [normalizedListings, applyFilters]);
  const sourceFacetSource = useMemo(() => applyFilters(normalizedListings, { source: true }), [normalizedListings, applyFilters]);
  const locationFacetSource = useMemo(() => applyFilters(normalizedListings, { location: true }), [normalizedListings, applyFilters]);
  const priceFacetSource = useMemo(() => applyFilters(normalizedListings, { price: true }), [normalizedListings, applyFilters]);

  const listingTypeOptions = useMemo(() => {
    const counts = { ungraded: 0, graded: 0 };
    listingTypeFacetSource.forEach((listing) => {
      counts[listing.listingType] += 1;
    });
    return counts;
  }, [listingTypeFacetSource]);

  const finishVariantOptions = useMemo(() => {
    const counts = {};
    finishFacetSource.forEach((listing) => {
      if (listing.listingType !== 'ungraded') return;
      counts[listing.finishVariant] = (counts[listing.finishVariant] || 0) + 1;
    });
    return counts;
  }, [finishFacetSource]);

  const gradeTree = useMemo(() => {
    const graderMap = new Map();
    gradeFacetSource.forEach((listing) => {
      if (listing.listingType !== 'graded') return;
      const graderLabel = listing.graderLabel || 'Unspecified';
      const gradeValueLabel = listing.gradeValueLabel || 'Unspecified';
      const graderKey = listing.gradeKeys?.grader || `grader:${graderLabel}`;
      const gradeKey = listing.gradeKeys?.grade || `grade:${graderLabel}|${gradeValueLabel}`;

      if (!graderMap.has(graderKey)) {
        graderMap.set(graderKey, {
          key: graderKey,
          label: graderLabel,
          count: 0,
          grades: new Map()
        });
      }

      const graderNode = graderMap.get(graderKey);
      graderNode.count += 1;

      if (!graderNode.grades.has(gradeKey)) {
        graderNode.grades.set(gradeKey, {
          key: gradeKey,
          label: gradeValueLabel,
          count: 0
        });
      }

      const gradeNode = graderNode.grades.get(gradeKey);
      gradeNode.count += 1;
    });

    const toArraySorted = (map) => Array.from(map.values()).sort((a, b) => b.count - a.count);
    return toArraySorted(graderMap).map((grader) => ({
      ...grader,
      grades: toArraySorted(grader.grades)
    }));
  }, [gradeFacetSource]);

  const availableGradeKeys = useMemo(() => {
    const keys = new Set();
    gradeTree.forEach((grader) => {
      keys.add(grader.key);
      grader.grades.forEach((grade) => keys.add(grade.key));
    });
    return keys;
  }, [gradeTree]);

  const conditionOptions = useMemo(() => {
    const counts = {};
    conditionFacetSource.forEach((listing) => {
      if (listing.listingType !== 'ungraded' || !listing.conditionLabel) return;
      counts[listing.conditionLabel] = (counts[listing.conditionLabel] || 0) + 1;
    });
    return counts;
  }, [conditionFacetSource]);

  const sourceTypeOptions = useMemo(() => {
    const counts = {};
    sourceFacetSource.forEach((listing) => {
      counts[listing.sourceType] = (counts[listing.sourceType] || 0) + 1;
    });
    return counts;
  }, [sourceFacetSource]);

  const locationTree = useMemo(() => {
    const countryMap = new Map();
    locationFacetSource.forEach((listing) => {
      const { country, region, city } = listing.locationParts;
      const countryKey = listing.locationKeys.country;
      const regionKey = listing.locationKeys.region;
      const cityKey = listing.locationKeys.city;

      if (!countryMap.has(countryKey)) {
        countryMap.set(countryKey, {
          key: countryKey,
          label: country,
          count: 0,
          regions: new Map()
        });
      }

      const countryNode = countryMap.get(countryKey);
      countryNode.count += 1;

      if (region === 'Unknown' || !regionKey) {
        return;
      }

      if (!countryNode.regions.has(regionKey)) {
        countryNode.regions.set(regionKey, {
          key: regionKey,
          label: region,
          count: 0,
          cities: new Map()
        });
      }

      const regionNode = countryNode.regions.get(regionKey);
      regionNode.count += 1;

      if (city === 'Unknown' || !cityKey) {
        return;
      }

      if (!regionNode.cities.has(cityKey)) {
        regionNode.cities.set(cityKey, {
          key: cityKey,
          label: city,
          count: 0
        });
      }

      const cityNode = regionNode.cities.get(cityKey);
      cityNode.count += 1;
    });

    const toArraySorted = (map) => Array.from(map.values()).sort((a, b) => b.count - a.count);

    const countries = toArraySorted(countryMap).map((country) => {
      const regions = toArraySorted(country.regions)
        .filter((region) => region.count >= 3)
        .map((region) => {
          const cities = toArraySorted(region.cities)
            .filter((city) => city.count >= 3);
          return { ...region, cities };
        });
      return { ...country, regions };
    });

    return countries;
  }, [locationFacetSource]);

  const availableLocationKeys = useMemo(() => {
    const keys = new Set();
    locationTree.forEach((country) => {
      keys.add(country.key);
      country.regions.forEach((region) => {
        keys.add(region.key);
        region.cities.forEach((city) => keys.add(city.key));
      });
    });
    return keys;
  }, [locationTree]);

  const availablePriceRange = useMemo(() => {
    const prices = priceFacetSource.map((listing) => listing.price).filter((price) => price !== null && price !== undefined && !isNaN(price));
    if (!prices.length) {
      return { min: 0, max: 100 };
    }
    return {
      min: roundPriceAmount(Math.min(...prices), 2),
      max: roundPriceAmount(Math.max(...prices), 2),
    };
  }, [priceFacetSource]);

  const availablePriceRangeDisplay = useMemo(() => {
    return {
      min: roundPriceAmount(convertCadAmount(availablePriceRange.min, currency), 2),
      max: roundPriceAmount(convertCadAmount(availablePriceRange.max, currency), 2),
    };
  }, [availablePriceRange.max, availablePriceRange.min, convertCadAmount, currency]);

  useEffect(() => {
    setSelectedFinishVariants((prev) => pruneSelection(prev, (value) => finishVariantOptions[value]));
  }, [finishVariantOptions]);

  useEffect(() => {
    setSelectedGrades((prev) => pruneSelection(prev, (value) => availableGradeKeys.has(value)));
  }, [availableGradeKeys]);

  // WO-35: prune remembered conditions / sellers only once this card's listings are in (an empty list while
  // loading would drop them); the stored preference itself is only changed by the shopper.
  useEffect(() => {
    if (!listingsLoaded) return;
    setSelectedConditions((prev) => pruneSelection(prev, (value) => conditionOptions[value]));
  }, [conditionOptions, listingsLoaded]);

  useEffect(() => {
    if (!listingsLoaded) return;
    setSelectedSourceTypes((prev) => pruneSelection(prev, (value) => sourceTypeOptions[value]));
  }, [sourceTypeOptions, listingsLoaded]);

  useEffect(() => {
    setSelectedLocations((prev) => pruneSelection(prev, (value) => availableLocationKeys.has(value)));
  }, [availableLocationKeys]);

  const hasUngradedSelection = selectedListingTypes.includes('ungraded');
  const hasGradedSelection = selectedListingTypes.includes('graded');

  const listingTypeItems = useMemo(() => {
    return [
      { key: 'ungraded', label: 'Ungraded', count: listingTypeOptions.ungraded || 0 },
      { key: 'graded', label: 'Graded', count: listingTypeOptions.graded || 0 }
    ].filter((item) => item.count > 0);
  }, [listingTypeOptions]);

  useEffect(() => {
    const availableKeys = listingTypeItems.map((item) => item.key);
    setSelectedListingTypes((prev) => {
      const next = prev.filter((key) => availableKeys.includes(key));
      if (next.length === 0 && availableKeys.length > 0) {
        return [availableKeys[0]];
      }
      if (next.length === prev.length && next.every((val, idx) => val === prev[idx])) {
        return prev;
      }
      return next;
    });
  }, [listingTypeItems]);

  const finishVariantItems = useMemo(() => {
    const order = ['General', 'Holofoil', 'Reverse', 'Unspecified'];
    return order
      .filter((variant) => finishVariantOptions[variant])
      .map((variant) => ({
        key: variant,
        label: variant,
        count: finishVariantOptions[variant]
      }));
  }, [finishVariantOptions]);

  const conditionItems = useMemo(() => {
    return Object.entries(conditionOptions)
      .map(([key, count]) => ({ key, label: key, count }))
      .sort((a, b) => b.count - a.count);
  }, [conditionOptions]);

  const sourceTypeItems = useMemo(() => {
    return Object.entries(sourceTypeOptions)
      .map(([key, count]) => ({ key, label: key, count }))
      .sort((a, b) => b.count - a.count);
  }, [sourceTypeOptions]);

  const toggleFilterSection = (key) => {
    setFilterOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleListingType = (type) => {
    setSelectedListingTypes((prev) => {
      const exists = prev.includes(type);
      if (exists && prev.length === 1) {
        return prev;
      }
      if (exists) {
        return prev.filter((item) => item !== type);
      }
      return [...prev, type];
    });
  };

  const toggleFinishVariant = (variant) => {
    setSelectedFinishVariants((prev) =>
      prev.includes(variant) ? prev.filter((item) => item !== variant) : [...prev, variant]
    );
  };

  const toggleGrade = (grade) => {
    setSelectedGrades((prev) =>
      prev.includes(grade) ? prev.filter((item) => item !== grade) : [...prev, grade]
    );
  };

  const toggleCondition = (condition) => {
    const next = selectedConditions.includes(condition)
      ? selectedConditions.filter((item) => item !== condition) : [...selectedConditions, condition];
    setSelectedConditions(next);
    savePrefs({ conditions: next });
  };

  const toggleSourceType = (sourceType) => {
    const next = selectedSourceTypes.includes(sourceType)
      ? selectedSourceTypes.filter((item) => item !== sourceType) : [...selectedSourceTypes, sourceType];
    setSelectedSourceTypes(next);
    savePrefs({ sellers: next });
  };

  const toggleLocationSelection = (key) => {
    setSelectedLocations((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  const toggleLocationOpen = (key) => {
    setLocationOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleGradeOpen = (key) => {
    setGradeOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const pruneSelection = (prev, predicate) => {
    const next = prev.filter(predicate);
    if (next.length === prev.length && next.every((value, index) => value === prev[index])) {
      return prev;
    }
    return next;
  };

  const handleAddToCollection = async () => {
    if (!user) {
      const returnUrl = `/card/${cardIdUrl}`
      sessionStorage.setItem('redirectAfterLogin', returnUrl)
      navigate('/login')
      return
    }

    if (inCollection) {
      // Remove flow — find which collection has this card and remove
      setActionLoading(true)
      try {
        const collectionResponse = await collectionAPI.getUserCollections(user.id)
        if (collectionResponse.data) {
          for (const collection of collectionResponse.data) {
            const itemsResponse = await collectionAPI.getCollectionItems(collection.id)
            if (itemsResponse.data?.items?.some(item => item.card_id === cardId)) {
              await collectionAPI.removeFromCollection(collection.id, cardId)
              setInCollection(false)
              break
            }
          }
        }
      } catch (error) {
        console.error('Error removing from collection:', error)
      } finally {
        setActionLoading(false)
      }
    } else {
      // Add flow — show collection selector first
      setShowCollectionSelector(true)
    }
  }

  const handleCollectionSelected = (collectionId) => {
    setSelectedCollectionId(collectionId)
    setShowCollectionSelector(false)
    // Now show price input modal
    setShowPriceInput(true)
  }

  const handlePriceInputConfirm = async ({ price, currency: inputCurrency, condition, quantity }) => {
    setShowPriceInput(false)
    setActionLoading(true)
    try {
      await collectionAPI.addToCollection(selectedCollectionId, cardId, {
        quantity,
        condition,
        purchase_price: price,
        currency: inputCurrency || currency,
      })
      setInCollection(true)
    } catch (error) {
      console.error('Error adding to collection:', error)
    } finally {
      setActionLoading(false)
      setSelectedCollectionId(null)
    }
  }

  const handleAddToWishlist = async () => {
    if (!user) {
      const returnUrl = `/card/${cardIdUrl}`
      sessionStorage.setItem('redirectAfterLogin', returnUrl)
      navigate('/login')
      return
    }

    if (inWishlist) {
      // Remove
      setActionLoading(true)
      try {
        await wishlistAPI.removeFromWishlist(cardId)
        setInWishlist(false)
      } catch (error) {
        console.error('Error removing from wishlist:', error)
      } finally {
        setActionLoading(false)
      }
    } else {
      // Show target price prompt
      setShowWishlistPricePrompt(true)
    }
  }

  const handleWishlistPriceConfirm = async (targetPrice) => {
    setShowWishlistPricePrompt(false)
    setActionLoading(true)
    try {
      await wishlistAPI.addToWishlist(cardId, {
        target_price: targetPrice,
        currency,
      })
      setInWishlist(true)
    } catch (error) {
      console.error('Error adding to wishlist:', error)
    } finally {
      setActionLoading(false)
    }
  }

  // Helper functions
  function formatVariantName(variant) {
    // Convert "1st-Edition-Holofoil" to "1st Edition Holofoil"
    return variant.replace(/-/g, ' ');
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  }

  function getVariantTooltip(variant) {
    const avgPriceCad = pricingData?.variants?.[variant]?.sources
      ? Object.values(pricingData.variants[variant].sources)[0]?.avg_price
      : null;

    const converted = convertCadAmount(avgPriceCad, currency);
    const formatted = formatCurrencyAmount(converted, currency, { fallback: 'N/A' });
    return variant + ': ' + formatted;
  }

  useEffect(() => {
    // Track exit when component unmounts
    return () => {
      // Track card view exit with actual duration
      if (cardId && cardData) {
        analyticsService.trackCardViewExit(cardId, cardData.setId || cardData.set_id || null, cardEntrySource);
      }
    };
  }, [cardEntrySource, cardId, cardData]);

  const isGradedView = viewMode === 'graded';
  const activeSources = isGradedView ? selectedGradedSources : selectedSources;
  const primarySource = activeSources[0] || 'market';
  const availableSources = historyData?.available_sources || pricingData?.history?.available_sources || [];
  const availableSourceOptions = availableSources.length > 0
    ? sourceOrder.filter((sourceKey) => availableSources.includes(sourceKey))
    : ['market'];
  const allSourcesSelected = availableSourceOptions.length > 0 && availableSourceOptions.every((src) => activeSources.includes(src));
  const showGradedToggle = gradedAvailability?.has_graded_data;
  const availableGradeBuckets = getAvailableGradeBuckets(gradedAvailability);
  const needsHistoryPrice = isGradedView || selectedFinish !== 'general';
  const historyLatestPrice = historyData?.summary?.latest_price;
  const displayPrice = needsHistoryPrice ? historyLatestPrice : pricingData?.general?.price;
  const priceIsLoading = needsHistoryPrice ? historyLoading : pricingLoading;

  if (loading) {
    return (
      <div id="loading" className="container mx-auto px-4 py-16 flex flex-col items-center justify-center">
        <PageMeta
          title={title}
          description={description}
          image={ogImage}
          url={canonicalUrl}
        />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple"></div>
        <p className="mt-4 text-cream/80">Loading card details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div id="errorState" className="container mx-auto px-4 py-16 text-center">
        <PageMeta
          title={title}
          description={description}
          image={ogImage}
          url={canonicalUrl}
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-purple">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h2 className="mt-4 text-xl font-medium text-cream">{notFound ? 'Card not found' : 'Unable to load card data'}</h2>
        <p className="mt-2 text-cream/60">{notFound
          ? 'We could not find this card. It may not be listed on Lilycove yet, or the link may be mistyped.'
          : 'Please try again later or check the card ID'}</p>
        <button onClick={() => window.location.href='/'} className="mt-6 inline-flex items-center justify-center rounded-md bg-purple text-cream py-2 px-4 font-medium hover:bg-purple/80 transition">
          Return Home
        </button>
      </div>
    );
  }

  return (
    <main id="cardContent" className="container mx-auto px-4 py-2">
      <PageMeta
        title={title}
        description={description}
        image={ogImage}
        url={canonicalUrl}
      />
      {/* Breadcrumbs */}
      {cardData && (
        <div className="flex items-center text-sm mb-6 text-cream/70">
          <Link to="/" className="text-skyblue hover:text-cream">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/series" className="text-skyblue hover:text-cream">Pokémon Series</Link>
          {cardData.seriesId && cardData.seriesName && (
            <>
              <span className="mx-2">/</span>
              <Link to={`/series/${encodeURIComponent(cardData.seriesId)}`} className="text-skyblue hover:text-cream">{cardData.seriesName}</Link>
            </>
          )}
          {cardData.setId && cardData.setName && (
            <>
              <span className="mx-2">/</span>
              <Link to={`/sets/${encodeURIComponent(cardData.setId)}`} className="text-skyblue hover:text-cream">{cardData.setName}</Link>
            </>
          )}
          <span className="mx-2">/</span>
        </div>
      )}

      {/* Card Header */}
      <div className="border-b border-purple/30 pb-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 id="cardName" className="text-2xl md:text-3xl font-medium text-purple">{cardData.name}</h1>
            <div className="flex flex-wrap items-center gap-1 mt-1 text-sm text-cream/80">
              <span id="cardRarity">{cardData.rarity}</span>
              <span>•</span>
              <span id="cardNumber">{`${cardData.number}/${cardData.printedTotal || cardData.set?.printedTotal || 'Unknown'}`}</span>
              <div id="cardVariants" className="flex gap-1 ml-2">
                {/* Show pricing variants if available from new API */}
                {pricingData?.variants && Object.keys(pricingData.variants).length > 0 ? (
                  Object.keys(pricingData.variants).map((variant, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center rounded-full bg-dark border border-purple/50 px-2 py-0.5 text-xs font-medium"
                      title={getVariantTooltip(variant)}
                    >
                      {formatVariantName(variant)}
                    </span>
                  ))
                ) : (
                  /* Fallback to static variants if new pricing data not available */
                  cardData.variants && Object.keys(cardData.variants).map((variant, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center rounded-full bg-dark border border-purple/50 px-2 py-0.5 text-xs font-medium"
                    >
                      {formatVariantName(variant)}
                    </span>
                  ))
                )}
                {cardData.superType && (
                  <span className="inline-flex items-center rounded-full bg-dark border border-purple/50 px-2 py-0.5 text-xs font-medium">
                    {cardData.superType}
                  </span>
                )}
                {cardData.types && cardData.types.map((type, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center rounded-full bg-dark border border-purple/50 px-2 py-0.5 text-xs font-medium"
                  >
                    {type}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Price and Action Buttons */}
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div id="cardPrice" className="text-2xl md:text-3xl font-bold text-green">
              {priceIsLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green"></div>
                  <span className="text-lg">Loading...</span>
                </div>
              ) : ((!needsHistoryPrice && pricingError) || !displayPrice || displayPrice <= 0) ? (
                <span className="text-lg text-cream/60">Price unavailable</span>
              ) : (
                <>
                  <PriceDisplay amountCad={displayPrice} />
                  <div className="text-xs text-cream/60 mt-1">
                    Market{pricingData?.general?.confidence ? ` • ${pricingData.general.confidence} confidence` : ''}
                  </div>
                </>
              )}
            </div>
            
            {/* Collection/Wishlist Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleAddToCollection}
                disabled={actionLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                  inCollection
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-purple hover:bg-purple/90 text-cream'
                }`}
                title={inCollection ? 'Remove from Collection' : 'Add to Collection'}
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                ) : inCollection ? (
                  <Check size={16} />
                ) : (
                  <Plus size={16} />
                )}
                <span className="hidden sm:inline">
                  {inCollection ? 'In Collection' : 'Add to Collection'}
                </span>
              </button>

              <button
                onClick={handleAddToWishlist}
                disabled={actionLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                  inWishlist
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-dark border border-purple/30 hover:bg-purple/20 text-cream'
                }`}
                title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
              >
                {actionLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                ) : (
                  <Heart size={16} className={inWishlist ? 'fill-current' : ''} />
                )}
                <span className="hidden sm:inline">
                  {inWishlist ? 'Wishlisted' : 'Add to Wishlist'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
        {/* Card Image - left column */}
        <div className="md:col-span-3 flex flex-col h-full">
          <div className="border border-purple/30 rounded-lg p-2 bg-[#1A1A20] flex-grow flex flex-col">
            <div className="max-w-[300px] mx-auto flex-grow flex flex-col justify-center">
              <div className="aspect-[2.5/3.5] relative rounded-md overflow-hidden border-2 border-purple/30">
                <img
                  id="cardImage"
                  src={getDisplayImageUrl(cardData)} // Use the helper function
                  alt={`${cardData.name} Pokémon Card`}
                  className="w-full h-full object-cover"
                  onError={imageFallback(sourceImageUrls(cardData))} // original URL, then local SVG placeholder
                />
              </div>

            </div>
          </div>
        </div>

        {/* Card Details - middle column */}
        <div className="md:col-span-5 flex flex-col h-full">
          <div className="w-full flex-grow flex flex-col">
            <div className="grid grid-cols-1 bg-dark border border-purple/30 rounded-md overflow-hidden">
              <button 
                className={`tab-trigger py-2 text-center cursor-pointer bg-transparent border-none text-cream ${activeTab === 'details' ? 'bg-purple' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Card Details
              </button>
            </div>
            
            <div className={`tab-content mt-4 ${activeTab === 'details' ? '' : 'hidden'}`} id="details">
              <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden">
                <div id="cardDetails" className="p-4 space-y-4">
                  {/* Card type details */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-cream/60">Card Type:</div>
                    <div className="text-cream">{cardData.types ? cardData.types.join(', ') : 'Unknown'}</div>
                  </div>
                  
                  {/* Stage details */}
                  {cardData.subType && cardData.subType.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-cream/60">Stage:</div>
                      <div className="text-cream">{cardData.subType.join(', ')}</div>
                    </div>
                  )}
                  
                  {/* HP details */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-cream/60">HP:</div>
                    <div className="text-cream">{cardData.hp || 'N/A'}</div>
                  </div>
                  
                  {/* Evolution chain */}
                  {cardData.evolvesFrom && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-cream/60">Evolves From:</div>
                      <div className="text-cream">{cardData.evolvesFrom}</div>
                    </div>
                  )}
                  
                  {/* Artist info */}
                  {cardData.artist && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-cream/60">Artist:</div>
                      <div className="text-cream">{cardData.artist}</div>
                    </div>
                  )}
                  
                  {/* Render attacks */}
                  {cardData.attacks && cardData.attacks.map((attack, index) => {
                    const attackCost = attack.cost.map(energy => {
                      return energy === 'Colorless' ? 'C' : energy.charAt(0);
                    }).join('');
                    
                    return (
                      <div key={index} className="border-t border-purple/20 pt-4">
                        <h3 className="font-medium mb-2 text-skyblue">Attack: [{attackCost}] {attack.name}</h3>
                        <p className="text-sm text-cream/80">{attack.text || attack.damage}</p>
                      </div>
                    );
                  })}
                  
                  {/* Footer section */}
                  <div className="border-t border-purple/20 pt-4">
                    {/* Weakness */}
                    {cardData.weaknesses && cardData.weaknesses.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <div className="text-cream/60">Weakness:</div>
                        <div className="text-cream">
                          {cardData.weaknesses.map(w => `${w.type} (${w.value})`).join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {/* Resistances */}
                    {cardData.resistances && cardData.resistances.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <div className="text-cream/60">Resistance:</div>
                        <div className="text-cream">
                          {cardData.resistances.map(r => `${r.type} (${r.value})`).join(', ')}
                        </div>
                      </div>
                    )}
                    
                    {/* Retreat cost */}
                    {cardData.retreatCost && (
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <div className="text-cream/60">Retreat Cost:</div>
                        <div className="text-cream">{cardData.convertedRetreatCost}</div>
                      </div>
                    )}
                    
                    {/* Rarity and card number */}
                    <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                      <div className="text-cream/60">Rarity:</div>
                      <div className="text-cream">{cardData.rarity}</div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                      <div className="text-cream/60">Card Number:</div>
                      <div className="text-cream">{`${cardData.number}/${cardData.printedTotal || cardData.set?.printedTotal || 'Unknown'}`}</div>
                    </div>
                    
                    {/* Release date */}
                    {cardData.releaseDate && (
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <div className="text-cream/60">Released:</div>
                        <div className="text-cream">{formatDate(cardData.releaseDate)}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`tab-content mt-4 ${activeTab === 'sales' ? '' : 'hidden'}`} id="sales">
              <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <p className="text-purple font-medium text-lg">Coming Soon</p>
                      <p className="text-cream/60 text-sm mt-2">Sales history will be available in a future update</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Price History - right column */}
        <div className="md:col-span-4 flex flex-col h-full">
          <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden flex-grow flex flex-col">
            <div className="px-4 py-3 border-b border-purple/30">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-medium text-cream">Historical Pricing</h2>
                <div className="relative group">
                  <Info className="w-4 h-4 text-cream/50" />
                  <div className="absolute left-1/2 bottom-full z-20 mb-2 w-64 -translate-x-1/2 rounded-md border border-purple/30 bg-[#1A1A20] px-3 py-2 text-xs text-cream/80 opacity-0 shadow-lg transition-opacity pointer-events-none group-hover:opacity-100">
                    Market Price is our estimate of what others have typically paid for this card.
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4">
              <div className="mb-4 flex flex-wrap items-center gap-3">
                {gradedLoading ? (
                  <div className="text-xs text-cream/60">Checking graded availability...</div>
                ) : showGradedToggle ? (
                  <div className="flex items-center bg-dark border border-purple/30 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('raw')}
                      className={`px-3 py-1 text-xs rounded transition ${
                        viewMode === 'raw'
                          ? 'bg-purple text-cream'
                          : 'text-cream/60 hover:text-cream'
                      }`}
                    >
                      Ungraded
                    </button>
                    <button
                      onClick={() => setViewMode('graded')}
                      className={`px-3 py-1 text-xs rounded transition ${
                        viewMode === 'graded'
                          ? 'bg-purple text-cream'
                          : 'text-cream/60 hover:text-cream'
                      }`}
                    >
                      Graded
                    </button>
                  </div>
                ) : null}

                {viewMode === 'graded' ? (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-cream/60">Grade</label>
                    <select
                      value={selectedGradeBucket || ''}
                      onChange={(e) => setSelectedGradeBucket(e.target.value)}
                      className="bg-dark border border-purple/30 rounded px-2 py-1 text-xs text-cream"
                    >
                      {availableGradeBuckets.length === 0 && (
                        <option value="" disabled>
                          Select grade
                        </option>
                      )}
                      {availableGradeBuckets.map((bucket) => (
                        <option key={bucket} value={bucket}>
                          {gradeBucketLabels[bucket] || bucket}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-cream/60">Finish</label>
                    <select
                      value={selectedFinish}
                      onChange={(e) => setSelectedFinish(e.target.value)}
                      className="bg-dark border border-purple/30 rounded px-2 py-1 text-xs text-cream"
                    >
                      <option value="general">General</option>
                      <option value="holofoil">Holofoil</option>
                      <option value="reverse">Reverse</option>
                    </select>
                  </div>
                )}

                <div className="ml-auto relative">
                  <button
                    type="button"
                    onClick={() => setSourcePanelOpen((prev) => !prev)}
                    className="flex items-center gap-2 px-3 py-1 rounded-lg border border-purple/30 bg-dark text-xs text-cream/80 hover:text-cream hover:bg-purple/10 transition"
                  >
                    <SlidersHorizontal className="h-3.5 w-3.5" />
                    Sources
                  </button>
                </div>
              </div>
              <div className="relative">
                {sourcePanelOpen && (
                  <div
                    className="absolute inset-0 z-20 bg-black/40"
                    onClick={() => setSourcePanelOpen(false)}
                  />
                )}
                <PriceChart 
                  cardId={cardIdUrl} 
                  cardName={cardData.name}
                  initialData={
                    !isGradedView && selectedFinish === 'general'
                      ? pricingData?.history
                      : null
                  }
                  bucket={isGradedView ? 'graded' : 'raw'}
                  finish={selectedFinish}
                  gradeBucket={selectedGradeBucket}
                  source={primarySource}
                  sources={activeSources}
                  onDataLoaded={handleHistoryLoaded}
                  onLoadingChange={handleHistoryLoading}
                  onDoubleClick={(data) => {
                    setEnlargedChartData(data);
                    setShowPriceHistoryModal(true);
                  }}
                />
                <div
                  ref={sourcePanelRef}
                  className={`absolute right-0 top-0 z-30 h-full w-64 border-l border-purple/30 bg-[#15151d] shadow-xl transition-transform duration-200 ${
                    sourcePanelOpen ? 'translate-x-0 pointer-events-auto' : 'translate-x-full pointer-events-none'
                  }`}
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-purple/20">
                    <span className="text-xs uppercase tracking-wide text-cream/60">Sources</span>
                    <button
                      type="button"
                      onClick={() => setSourcePanelOpen(false)}
                      className="text-cream/60 hover:text-cream text-sm"
                    >
                      Close
                    </button>
                  </div>
                  <div className="p-4 space-y-2">
                    <button
                      type="button"
                      onClick={() => toggleSourceSelection('all')}
                      className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-xs transition ${
                        allSourcesSelected
                          ? 'bg-purple text-cream'
                          : 'bg-dark text-cream/70 hover:text-cream'
                      }`}
                    >
                      <span>Show All</span>
                      <span className="text-[10px]">{allSourcesSelected ? 'On' : 'Off'}</span>
                    </button>
                    {availableSourceOptions.map((sourceKey) => {
                      const active = activeSources.includes(sourceKey);
                      return (
                        <button
                          key={sourceKey}
                          type="button"
                          onClick={() => toggleSourceSelection(sourceKey)}
                          className={`w-full flex items-center justify-between rounded-md px-3 py-2 text-xs transition ${
                            active ? 'bg-purple/20 text-cream' : 'bg-dark text-cream/60 hover:text-cream'
                          }`}
                        >
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: sourceColors[sourceKey] || '#6B7280' }} />
                            {sourceLabels[sourceKey] || sourceKey}
                          </span>
                          <span className="text-[10px]">{active ? 'On' : 'Off'}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Listings Section */}
      <div className="mt-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-[#1A1A20] border border-purple/30 rounded-lg p-4">
              <h2 className="text-cream text-lg font-medium mb-4">Filter</h2>

              {/* Listing Type Filter */}
              {listingTypeItems.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('listingType')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Listing Type</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.listingType ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.listingType && (
                    <div className="space-y-2">
                      {listingTypeItems.map((item) => (
                        <label key={item.key} className="flex items-center justify-between">
                          <span className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedListingTypes.includes(item.key)}
                              onChange={() => toggleListingType(item.key)}
                              className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                            />
                            <span className="ml-2 text-cream/80 text-sm">{item.label}</span>
                          </span>
                          <span className="text-xs text-cream/60">{item.count}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Search Filter */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for a listing..."
                    value={listingSearch}
                    onChange={(e) => setListingSearch(e.target.value)}
                    className="w-full bg-dark border border-purple/30 rounded-lg px-4 py-2 text-cream focus:outline-none focus:border-purple"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-cream/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Variant Filter */}
              {hasUngradedSelection && finishVariantItems.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('variant')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Variant</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.variant ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.variant && (
                    <div className="space-y-2">
                      {finishVariantItems.map((variant) => (
                        <label key={variant.key} className="flex items-center justify-between">
                          <span className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedFinishVariants.includes(variant.key)}
                              onChange={() => toggleFinishVariant(variant.key)}
                              className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                            />
                            <span className="ml-2 text-cream/80 text-sm">{variant.label}</span>
                          </span>
                          <span className="text-xs text-cream/60">{variant.count}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Grade Filter */}
              {hasGradedSelection && gradeTree.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('grade')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Grade</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.grade ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.grade && (
                    <div className="space-y-2">
                      {gradeTree.map((grader) => (
                        <div key={grader.key}>
                          <div className="flex items-center justify-between">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedGrades.includes(grader.key)}
                                onChange={() => toggleGrade(grader.key)}
                                className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                              />
                              <span className="ml-2 text-cream/80 text-sm">{grader.label}</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-cream/60">{grader.count}</span>
                              {grader.grades.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleGradeOpen(grader.key)}
                                  className="text-xs text-cream/60"
                                >
                                  {gradeOpen[grader.key] ? '–' : '+'}
                                </button>
                              )}
                            </div>
                          </div>
                          {grader.grades.length > 0 && gradeOpen[grader.key] && (
                            <div className="ml-4 mt-2 space-y-2">
                              {grader.grades.map((grade) => (
                                <label key={grade.key} className="flex items-center justify-between">
                                  <span className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={selectedGrades.includes(grade.key)}
                                      onChange={() => toggleGrade(grade.key)}
                                      className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                                    />
                                    <span className="ml-2 text-cream/80 text-sm">{grade.label}</span>
                                  </span>
                                  <span className="text-xs text-cream/60">{grade.count}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Condition Filter */}
              {hasUngradedSelection && conditionItems.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('condition')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Condition</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.condition ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.condition && (
                    <div className="space-y-2">
                      {conditionItems.map((condition) => (
                        <label key={condition.key} className="flex items-center justify-between">
                          <span className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedConditions.includes(condition.key)}
                              onChange={() => toggleCondition(condition.key)}
                              className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                            />
                            <span className="ml-2 text-cream/80 text-sm">{condition.label}</span>
                          </span>
                          <span className="text-xs text-cream/60">{condition.count}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Source Type Filter */}
              {sourceTypeItems.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('source')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Source Type</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.source ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.source && (
                    <div className="space-y-2">
                      {sourceTypeItems.map((source) => (
                        <label key={source.key} className="flex items-center justify-between">
                          <span className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedSourceTypes.includes(source.key)}
                              onChange={() => toggleSourceType(source.key)}
                              className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                            />
                            <span className="ml-2 text-cream/80 text-sm">{source.label}</span>
                          </span>
                          <span className="text-xs text-cream/60">{source.count}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Near Me Filter (WO-13) */}
              <NearMeFilter
                open={filterOpen.nearMe}
                onToggle={() => toggleFilterSection('nearMe')}
                profile={profile}
                nearMe={{ ...nearMe, status: nearMeStatus }}
                onChange={({ status, ...next }) => setNearMe(next)}
                matchCount={nearbyById ? `${Object.keys(nearbyById).length} listings` : ''}
              />

              {/* Seller Location Filter */}
              {locationTree.length > 0 && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('location')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Seller Location</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.location ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.location && (
                    <div className="space-y-2">
                      {locationTree.map((country) => (
                        <div key={country.key}>
                          <div className="flex items-center justify-between">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedLocations.includes(country.key)}
                                onChange={() => toggleLocationSelection(country.key)}
                                className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                              />
                              <span className="ml-2 text-cream/80 text-sm">{country.label}</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-cream/60">{country.count}</span>
                              {country.regions.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => toggleLocationOpen(country.key)}
                                  className="text-xs text-cream/60"
                                >
                                  {locationOpen[country.key] ? '–' : '+'}
                                </button>
                              )}
                            </div>
                          </div>
                          {country.regions.length > 0 && locationOpen[country.key] && (
                            <div className="ml-4 mt-2 space-y-2">
                              {country.regions.map((region) => (
                                <div key={region.key}>
                                  <div className="flex items-center justify-between">
                                    <label className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={selectedLocations.includes(region.key)}
                                        onChange={() => toggleLocationSelection(region.key)}
                                        className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                                      />
                                      <span className="ml-2 text-cream/80 text-sm">{region.label}</span>
                                    </label>
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-cream/60">{region.count}</span>
                                      {region.cities.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => toggleLocationOpen(region.key)}
                                          className="text-xs text-cream/60"
                                        >
                                          {locationOpen[region.key] ? '–' : '+'}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  {region.cities.length > 0 && locationOpen[region.key] && (
                                    <div className="ml-4 mt-2 space-y-2">
                                      {region.cities.map((city) => (
                                        <label key={city.key} className="flex items-center justify-between">
                                          <span className="flex items-center">
                                            <input
                                              type="checkbox"
                                              checked={selectedLocations.includes(city.key)}
                                              onChange={() => toggleLocationSelection(city.key)}
                                              className="form-checkbox text-purple rounded border-purple/30 bg-dark"
                                            />
                                            <span className="ml-2 text-cream/80 text-sm">{city.label}</span>
                                          </span>
                                          <span className="text-xs text-cream/60">{city.count}</span>
                                        </label>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Price Range Filter */}
              {availablePriceRange.min !== availablePriceRange.max && (
                <div className="mb-6">
                  <button
                    onClick={() => toggleFilterSection('price')}
                    className="w-full flex items-center justify-between text-cream font-medium mb-2"
                  >
                    <span>Price</span>
                    <span className="text-xs text-cream/60">
                      {filterOpen.price ? '–' : '+'}
                    </span>
                  </button>
                  {filterOpen.price && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder={'Min (' + formatCurrencyAmount(availablePriceRangeDisplay.min, currency) + ')'}
                          value={priceRange.min}
                          min={availablePriceRangeDisplay.min}
                          max={availablePriceRangeDisplay.max}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                          className="w-full bg-dark border border-purple/30 rounded-lg px-3 py-1 text-cream text-sm"
                        />
                        <input
                          type="number"
                          step="0.01"
                          placeholder={'Max (' + formatCurrencyAmount(availablePriceRangeDisplay.max, currency) + ')'}
                          value={priceRange.max}
                          min={availablePriceRangeDisplay.min}
                          max={availablePriceRangeDisplay.max}
                          onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                          className="w-full bg-dark border border-purple/30 rounded-lg px-3 py-1 text-cream text-sm"
                        />
                      </div>
                      {availablePriceRange.min !== 0 && availablePriceRange.max !== 100 && (
                        <div className="text-xs text-cream/60 mt-1">
                          Range: <PriceDisplay amountCad={availablePriceRange.min} /> - <PriceDisplay amountCad={availablePriceRange.max} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Listings Grid */}
          <div className="md:col-span-3">
            <div className="bg-[#1A1A20] border border-purple/30 rounded-lg overflow-hidden">
              <div className="bg-purple/20 p-4 border-b border-purple/30">
                <div className="flex flex-wrap gap-2 justify-between items-center">
                  <h2 className="font-medium text-cream">
                    Listings: <span>{filteredListings.length} of {listings.length}</span> Results
                  </h2>
                  <div className="flex flex-wrap items-center gap-2">
                  <NearMeChip profile={profile} nearMe={nearMe} onChange={setNearMe} status={nearMeStatus} wanted={nearMeWanted} />
                  <select
                    aria-label="Sort listings"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="bg-dark border border-purple/30 rounded-lg px-3 py-1 text-cream text-sm"
                  >
                    <option>Most Recent</option>
                    <option>Price: Low to High</option>
                    <option>Price: High to Low</option>
                    {nearMe.active && <option>Nearest</option>}
                  </select>
                  </div>
                </div>
              </div>

                <div className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {listings.length === 0 ? (
                    <div className="p-4 flex items-center justify-center text-cream/60">
                      {listingsLoaded ? 'No listings available at this time' : 'Loading listings…'}
                    </div>
                  ) : filteredListings.length === 0 ? (
                    <div className="p-4 flex items-center justify-center text-cream/60">
                      {nearMe.active && nearbyById && Object.keys(nearbyById).length === 0
                        ? `No store listings within ${nearMe.radiusKm} km of ${nearMe.label || 'you'} for this card. Try a larger radius or turn off near me.`
                        : 'No listings match the selected filters'}
                    </div>
                  ) : (
                    [...filteredListings]
                      .sort((a, b) => {
                        if (sortOrder === 'Most Recent') {
                          const dateA = a.date_listed ? new Date(a.date_listed).getTime() : 0;
                          const dateB = b.date_listed ? new Date(b.date_listed).getTime() : 0;
                          return dateB - dateA;
                        } else if (sortOrder === 'Price: Low to High') {
                          return (a.price || 0) - (b.price || 0);
                        } else if (sortOrder === 'Nearest') {
                          const distA = Number.isFinite(a.distance_km) ? a.distance_km : Infinity;
                          const distB = Number.isFinite(b.distance_km) ? b.distance_km : Infinity;
                          return distA - distB || (a.price || 0) - (b.price || 0);
                        } else {
                          return (b.price || 0) - (a.price || 0);
                        }
                      })
                      .map(listing => (
                        <a
                          key={listing.itemId}
                          href={listing.itemWebUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="border border-purple/30 rounded-lg overflow-hidden bg-dark hover:border-purple/60 transition-all cursor-pointer block"
                          onClick={() => {
                            const channel = (listing.source || listing.marketplace || 'unknown').toString().toLowerCase();
                            const storeName = (typeof listing.store === 'object' && listing.store?.name)
                              ? listing.store.name
                              : listing.store || listing.seller || channel;
                            const listingId = (
                              listing.itemId ||
                              listing.id ||
                              listing.listingId ||
                              listing.listing_id ||
                              listing.offerId ||
                              listing.sku ||
                              null
                            );
                            const listingUrl = listing.itemWebUrl || listing.url || listing.listing_url || null;
                            const storeId = listing.store_id || listing.storeId || listing.shopify_store_id || null;
                            const condition = listing.conditionLabel || listing.condition || listing.gradeLabel || null;
                            const numericPriceCad = Number.isFinite(Number(listing.price)) ? Number(listing.price) : null;
                            const displayPrice = numericPriceCad !== null
                              ? roundPriceAmount(convertCadAmount(numericPriceCad, currency), 2)
                              : null;

                            analyticsService.trackMarketplaceClick(
                              storeName || channel,
                              cardData?.id || cardData?.cardId || cardId,
                              listingId,
                              numericPriceCad,
                              {
                                channel,
                                store_id: storeId,
                                store_name: storeName || null,
                                listing_id: listingId,
                                listing_url: listingUrl,
                                condition,
                                price_cad: numericPriceCad,
                                display_currency: currency,
                                display_price: displayPrice,
                              }
                            ).catch(err => console.warn('Analytics tracking failed:', err));
                          }}
                        >
                          <div className="relative">
                            {listing.source === 'shopify' && listing.imageUrl ? (
                              // WO-35: a store's product photo (white background) in the shared store-photo frame;
                              // eBay sellers' own photos stay full-bleed as before
                              <div className="w-full aspect-[2.5/3.5]">
                                <StorePhoto url={listing.imageUrl} alt={listing.title} variant="card" testId="listing-store-photo"
                                            onError={(e) => { e.currentTarget.srcset = ''; e.currentTarget.src = '/placeholder-card.svg'; }} />
                              </div>
                            ) : (
                              <img
                                src={listing.imageUrl || '/placeholder-card.svg'}
                                alt={listing.title}
                                className="w-full aspect-[2.5/3.5] object-cover"
                              />
                            )}
                            {/* Store Logo Overlay - bigger and not cropped for transparent backgrounds */}
                            {listing.store_logo_url && (
                              <div className="absolute top-2 right-2">
                                <img
                                  src={listing.store_logo_url}
                                  alt={listing.store || 'Store logo'}
                                  className="w-16 h-16 object-contain"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              </div>
                            )}
                            <div className="absolute top-2 left-2">
                              <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                                listing.source === 'ebay' ? 'bg-dark/70 backdrop-blur-sm text-cream' : 'bg-dark/70 backdrop-blur-sm text-cream'
                              }`}>
                                {listing.source === 'ebay' ? 'eBay' : 'Store'}
                              </span>
                            </div>
                          </div>

                          <div className="p-3">
                            <h3 className="text-sm font-medium text-cream truncate">{listing.title}</h3>
                            <div className="mt-1 text-xs text-cream/60">
                              {listing.listingType === 'graded' ? (
                                <span className="inline-block">{listing.gradeLabel || 'Graded'}</span>
                              ) : (
                                listing.conditionLabel && (
                                  <span className="inline-block">{listing.conditionLabel}</span>
                                )
                              )}
                              {/* Show store name for Shopify listings, location for eBay */}
                              {listing.source === 'shopify' && listing.store && (
                                <span className="inline-block ml-2">{listing.store}</span>
                              )}
                              {listing.source === 'shopify' && Number.isFinite(listing.distance_km) && (
                                <span
                                  className="inline-block ml-2 text-skyblue/80"
                                  title={[listing.store_location?.name, listing.store_location?.address].filter(Boolean).join(' · ')}
                                >
                                  {formatDistanceKm(listing.distance_km)}
                                  {listing.store_location?.city ? ` · ${listing.store_location.city}` : ''}
                                </span>
                              )}
                              {listing.source === 'ebay' && (
                                <span className="inline-block ml-2">
                                  {(() => {
                                    const { city, region, country } = listing.locationParts || {};
                                    if (city && city !== 'Unknown' && region && region !== 'Unknown') {
                                      return `${city}, ${region}`;
                                    }
                                    if (region && region !== 'Unknown') {
                                      return region;
                                    }
                                    if (country && country !== 'Unknown') {
                                      return country;
                                    }
                                    return listing.location || 'Unknown';
                                  })()}
                                </span>
                              )}
                            </div>

                            <div className="mt-2 flex items-center justify-between">
                              <div className="text-skyblue font-medium">
                                <PriceDisplay amountCad={listing.price} fallback="Price unavailable" />
                              </div>
                            </div>
                            {listing.source === 'ebay' && checkedAgo(listing.last_checked) && (
                              <div className="mt-1 text-[11px] text-cream/40" title={checkedTitle(listing.last_checked)}>
                                {checkedAgo(listing.last_checked)}
                              </div>
                            )}
                          </div>
                        </a>
                      ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price History Modal */}
      <PriceHistoryModal 
        isOpen={showPriceHistoryModal}
        onClose={() => {
          setShowPriceHistoryModal(false);
          setEnlargedChartData(null);
        }}
        cardId={cardIdUrl}
        cardName={cardData?.name || 'Unknown Card'}
        initialData={
          enlargedChartData ||
          (!isGradedView && selectedFinish === 'general' ? pricingData?.history : null)
        }
        bucket={isGradedView ? 'graded' : 'raw'}
        finish={selectedFinish}
        gradeBucket={selectedGradeBucket}
        source={primarySource}
        sources={activeSources}
        hasGradedData={!!gradedAvailability?.has_graded_data}
      />

      {/* Collection Selector Modal */}
      <CollectionSelectorModal
        isOpen={showCollectionSelector}
        onClose={() => setShowCollectionSelector(false)}
        onSelectCollection={handleCollectionSelected}
        cardId={cardId}
        cardName={cardData?.name || 'this card'}
      />

      {/* Price Input Modal (after collection selected) */}
      <PriceInputModal
        isOpen={showPriceInput}
        onClose={() => { setShowPriceInput(false); setSelectedCollectionId(null) }}
        onConfirm={handlePriceInputConfirm}
        cardId={cardId}
        cardName={cardData?.name || 'this card'}
        currentPrice={pricingData?.general?.price ?? null}
      />

      {/* Wishlist Target Price Prompt */}
      {showWishlistPricePrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark border border-purple/20 rounded-lg max-w-sm w-full">
            <div className="p-4 border-b border-purple/20">
              <h2 className="text-lg font-semibold text-cream">Add to Wishlist</h2>
              <p className="text-sm text-gray-400 mt-1">{cardData?.name || 'this card'}</p>
            </div>
            <div className="p-4 space-y-4">
              {pricingData?.general?.price && (
                <div className="bg-gray-800/50 rounded-lg p-3">
                  <span className="text-sm text-gray-400">Current Market Price</span>
                  <PriceDisplay amountCad={pricingData.general.price} className="text-xl font-bold text-green-400" />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Target Price (optional)</label>
                <p className="text-xs text-gray-500 mb-2">Set a target buy price to track against market value.</p>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                  <input
                    id="wishlist-target-price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="e.g. 5.00"
                    className="w-full pl-8 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-purple/20">
              <button
                onClick={() => setShowWishlistPricePrompt(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
              >Cancel</button>
              <button
                onClick={() => {
                  const val = document.getElementById('wishlist-target-price')?.value
                  handleWishlistPriceConfirm(val ? parseFloat(val) : null)
                }}
                className="flex-1 px-4 py-2 bg-purple hover:bg-purple/90 text-white rounded-lg transition-colors"
              >Add to Wishlist</button>
            </div>
          </div>
        </div>
      )}
      <SeoExtras />
    </main>
  );
}

// WO-21: /card/<id> for every game. Namespaced product ids (ygo.lob-en001, mtg.*, ...) are singles served by
// /api/products/<id> and render the game-aware GameCardPage; legacy Pokemon ids (no '.') render CardDetail unchanged.
function Card() {
  const { cardId } = useParams();
  // WO-35: namespaced ids are lowercase (ygo.lob-en001); /card/YGO.LOB-EN001 goes to the real page
  const lower = String(cardId || '').toLowerCase();
  if (lower !== cardId && isProductCardId(lower)) return <Navigate replace to={`/card/${encodeURIComponent(lower)}`} />;
  if (isProductCardId(cardId)) return <GameCardPage key={cardId} productId={cardId} />;
  // WO-35: keyed like GameCardPage, so moving to another card starts clean (before, the previous card's name,
  // listings and card-specific filters stayed on screen until the new card loaded); remembered preferences
  // (lib/listingPrefs.js) are applied again on mount.
  return <CardDetail key={cardId} />;
}

export default Card;
